import { Fragment, memo, MouseEventHandler, useMemo, useReducer } from 'react';

import clsx from 'clsx';
import { useAtom } from 'jotai';
import { ImagePlus, X } from 'lucide-react';
import { isEmpty, isNonNullish } from 'remeda';
import { toast } from 'sonner';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';

import css from './FileSelector.module.css';

import { selectedFileAtom } from 'v2/atoms/fileSystemAtom';
import { FileSystem } from 'v2/components/fileSystem/FileSystem';
import { fileSrc } from 'v2/utils';

interface FileSelectorProps {
  value: string;
  label: string;
  error?: string;
  onSelect?: (value: string) => void;
  required?: boolean;
  maxSize?: number;
}

const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

export const FileSelector = memo<FileSelectorProps>(({ value, label, error, onSelect, required, maxSize }) => {
  const [selectedFile, setSelectedFile] = useAtom(selectedFileAtom);

  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const isThereFile = useMemo(() => {
    return !isEmpty(value);
  }, [value]);

  const handleClose = () => {
    setOpen();
    setSelectedFile(null);
  };

  const handleSave = () => {
    if (isNonNullish(selectedFile)) {
      if (isNonNullish(maxSize) && selectedFile.length > maxSize) {
        toast.error(`File size must not exceed ${formatBytes(maxSize)}`);
        return;
      }

      onSelect?.(selectedFile.id);

      handleClose();
    }
  };

  const handleClear: MouseEventHandler = (e) => {
    e.stopPropagation();

    onSelect?.('');
  };

  return (
    <Fragment>
      <div className={clsx(css.fileSelector, error && css.error)} onClick={setOpen}>
        <p className={css.fileSelectorLabel}>
          {label} {required && '*'}
          {isNonNullish(maxSize) && <span className={css.fileSelectorMaxSize}>(max {formatBytes(maxSize)})</span>}
        </p>

        <div className={css.fileSelectorImage}>
          {isThereFile ? <img src={fileSrc(value)} alt={label} /> : <ImagePlus width={30} height={30} opacity={0.5} />}

          {isThereFile && (
            <IconButton className={css.fileSelectorClearButton} onClick={handleClear}>
              <X width={20} height={20} color="white" />
            </IconButton>
          )}
        </div>
      </div>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>File System</DialogTitle>

        <DialogContent>
          <FileSystem />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
});

FileSelector.displayName = 'FileSelector';
